import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
} from "@chakra-ui/react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { AddIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import Loading from "components/Loading/Loading";
import SearchTable2 from "components/Tables/SearchTable2";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { ROUTES } from "constants/routes-path";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { daysOfWeek, userAccessTimesService } from "../../../../services/userAccessTimesService";
import ListPageCardUserHeader from "../ListPageCardUserHeader";
import { userService } from "../../../../services/userService";

const UserAccessTimeList = () => {
  const { t } = useTranslation();
  const [urlSearchParams] = useSearchParams();
  const { id } = useParams();
  const dialogRef = useRef(undefined);
  const [response, setResponse] = useState(undefined);
  const [userData, setUserData] = useState(undefined);
  const [searchParams, setSearchParams] = useState({});
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const [userAccessTimeToDelete, setUserAccessTimeToDelete] = useState(null);
  const translationKey = "settings.user-access-times";

  const parentId = urlSearchParams.get("parent_id");


  const handleRemove = (userAccessTimeId) => {
    setUserAccessTimeToDelete(userAccessTimeId);
    dialogRef.current?.displayDialog({
      title: t(`${translationKey}.list.actions.remove`),
      message: t("system.actions.remove-confirm"),
    });
  };

  const handleConfirmRemove = async () => {
    if (userAccessTimeToDelete) {
      try {
        await userAccessTimesService.delete(userAccessTimeToDelete);
        showToast({ title: t(`${translationKey}.messages.success.delete`), status: "success" });
        fetchData();
      } catch (err) {
        showToast({ title: t(err), status: "error" });
      }
    }
  };

  const getDayOfWeek = (value) => {
    return t(`${translationKey}.new.days-of-week.${daysOfWeek.find(d => d.value === value.toString()).label}`);
  }

  const columns = useMemo(() => ([
    {
      Header: t(`${translationKey}.list.table.header.day-of-week-start`),
      accessor: "dia_semana_inicio",
      custom: ({ value }) => getDayOfWeek(value),
    },
    {
      Header: t(`${translationKey}.list.table.header.day-of-week-end`),
      accessor: "dia_semana_fim",
      custom: ({ value }) => getDayOfWeek(value),
    },
    {
      Header: t(`${translationKey}.list.table.header.time-start`),
      accessor: "hora_inicio",
    },
    {
      Header: t(`${translationKey}.list.table.header.time-end`),
      accessor: "hora_fim",
    },
    {
      Header: t("system.labels.actions"),
      disableSortBy: true,
      actions: [
        {
          label: t('system.actions.edit'),
          onClick: (row) => {
            navigate(`${ROUTES.usersAccessTimeEditPath}/${row.original.id}?parent_id=${parentId}`, {
              state: { parentId }
            });
          },
          icon: <FaEdit />
        },
        {
          label: t('system.actions.remove'),
          onClick: (row) => { handleRemove(row.original.id) },
          icon: <FaTrashAlt color="#E53E3E" />,
        }
      ]
    }
  ]), [t]);


  const fetchData = async (params = {}) => {
    params.orderBy = params.orderBy ?? "data_criacao";
    params.orderDirection = params.orderDirection ?? "desc";
    params.page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
    if (response) setResponse(undefined);

    try {
      params = { ...searchParams, ...params, 'filters': { "id_usuario_horario": parentId }};
      setSearchParams(params);
      const data = await userAccessTimesService.list(params);
      const user = await userService.find(parentId);
      setUserData(user);
      setResponse(data);
    } catch (err) {
      setResponse(getEmptyPageResponse(searchParams));
      showToast({ title: t(err || `${translationKey}.messages.errors.list`), status: "error" });
    }
  };

  useEffect(() => {
    fetchData();
  }, [parentId]);

  return (
      <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
        <Flex w="100%" px="30px" justify="space-between" align="center" gap="4" direction={{ sm: "column", md: "row" }}>
          <Button
              variant="list-primary"
              leftIcon={<AddIcon fontSize="sm" />}
              alignSelf={{ sm: "flex-start", lg: null }}
              onClick={() => {
                navigate(`${ROUTES.usersAccessTimeAddPath}?parent_id=${parentId}`);
              }}
          >
            {t(`${translationKey}.list.actions.new`)}
          </Button>
        </Flex>

        <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
          {userData && (
              <ListPageCardUserHeader userData={userData} />
          )}
          <ListPageCardHeader title={t(`${translationKey}.title`)} subtitle={t(`${translationKey}.description`)} />

          <CardBody>
            {response !== undefined ? (
                <Box position="relative">
                  <SearchTable2
                      columnsData={columns}
                      tableData={response.content}
                      pageableData={toTablePageableData(response, { defaultField: "id", ...searchParams })}
                      onChange={fetchData}
                      disableSearch={true}
                  />
                </Box>
            ) : (
                <Loading maxH={{ sm: "100px", md: "350px" }} />
            )}
          </CardBody>
        </Card>

        <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
      </Flex>
  );
};

export default UserAccessTimeList;
